import { IconBrowser } from '@tabler/icons-react';
import { NavItemType } from 'types';
import { fetchGetExtensions } from 'api/extensions';
// trigger deploy
interface ExternalApp {
  id: string | number;
  name: string;
  url: string;
  description?: string;
  isActive: boolean;
}

// Cache for extensions to avoid multiple API calls
let extensionsCache: ExternalApp[] = [];
let cacheTimestamp: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// אייקונים לסוגי אפליקציות שונות
const getAppIcon = (appName: string) => {
  const name = appName.toLowerCase();
  
  // ניתן להוסיף לוגיקה מתוחכמת יותר כדי לבחור אייקון מתאים
  if (name.includes('chat') || name.includes('צ\'אט')) return IconBrowser;
  if (name.includes('calendar') || name.includes('יומן')) return IconBrowser;
  if (name.includes('mail') || name.includes('מייל')) return IconBrowser;
  if (name.includes('crm')) return IconBrowser;
  
  // אייקון ברירת מחדל
  return IconBrowser;
};

// טעינת הרחבות מהשרת
const loadExtensionsFromServer = async (): Promise<ExternalApp[]> => {
  try {
    const extensions = await fetchGetExtensions();
    const apps: ExternalApp[] = [];
    
    for (const ext of extensions) {
      const id = ext.id ?? ext.Id;
      const name = ext.name ?? ext.Name;
      const url = ext.url ?? ext.Url;
      const description = ext.description ?? ext.Description;
      const isActive = ext.isActive ?? ext.IsActive;
      
      if (id === undefined || id === null) {
        console.error('Extension missing id:', ext);
        continue;
      }
      
      apps.push({
        id: id.toString(),
        name: name || '',
        url: url || '',
        description: description,
        isActive: isActive ?? true
      });
    }
    
    return apps;
  } catch (error) {
    console.error('שגיאה בטעינת הרחבות מהשרת:', error);
    return [];
  }
};

// טעינת הרחבות עם cache
const getExtensionsWithCache = async (): Promise<ExternalApp[]> => {
  const now = Date.now();
  
  // אם ה-cache עדיין תקף, החזר אותו
  if (extensionsCache.length > 0 && (now - cacheTimestamp) < CACHE_DURATION) {
    return extensionsCache;
  }
  
  // אחרת, טען מהשרת ועדכן את ה-cache
  extensionsCache = await loadExtensionsFromServer();
  cacheTimestamp = now;
  return extensionsCache;
};

// פונקציה סינכרונית שמחזירה את ה-cache (לשימוש מיידי)
export const getDynamicExternalApps = (): NavItemType[] => {
  try {
    const activeApps = extensionsCache.filter(app => app.isActive);

    return activeApps.map(app => ({
      id: `external-app-${app.id}`,
      title: app.name,
      icon: getAppIcon(app.name),
      type: 'item',
      url: `/external-app/${app.id}`,
      noTranslation: true,
    }));
  } catch (error) {
    console.error('שגיאה בטעינת אפליקציות חיצוניות לתפריט:', error);
    return [];
  }
};

// פונקציה אסינכרונית לטעינת הרחבות מהשרת
export const loadDynamicExternalApps = async (): Promise<void> => {
  await getExtensionsWithCache();
  // הפעלת event לעדכון התפריט
  window.dispatchEvent(new CustomEvent('externalAppsUpdated'));
};

// פונקציה שמוסיפה אפליקציה חיצונית לתפריט הצדדי
export const addExternalAppToMenu = (app: ExternalApp) => {
  try {
    // עדכון ה-cache
    const existingIndex = extensionsCache.findIndex(existingApp => 
      existingApp.id.toString() === app.id.toString()
    );
    
    if (existingIndex >= 0) {
      extensionsCache[existingIndex] = app;
    } else {
      extensionsCache.push(app);
    }
    
    // עדכון ה-timestamp כדי שה-cache יישאר תקף
    cacheTimestamp = Date.now();
    
    // הפעלת event מותאם אישית כדי לעדכן את התפריט
    // שימוש ב-setTimeout כדי לוודא שהאירוע מופעל אחרי שהקומפוננטה מוכנה
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('externalAppsUpdated'));
    }, 0);
    
  } catch (error) {
    console.error('שגיאה בהוספת אפליקציה לתפריט:', error);
  }
};

// פונקציה שמסירה אפליקציה חיצונית מהתפריט הצדדי
export const removeExternalAppFromMenu = (appId: string) => {
  try {
    extensionsCache = extensionsCache.filter(app => app.id.toString() !== appId);
    
    // עדכון ה-timestamp
    cacheTimestamp = Date.now();
    
    // הפעלת event מותאם אישית כדי לעדכן את התפריט
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('externalAppsUpdated'));
    }, 0);
    
  } catch (error) {
    console.error('שגיאה בהסרת אפליקציה מהתפריט:', error);
  }
};

// פונקציה לאיפוס ה-cache (לשימוש אחרי עדכונים)
export const invalidateExtensionsCache = () => {
  extensionsCache = [];
  cacheTimestamp = 0;
};
